import { FC, useCallback, useMemo, useState } from 'react';
import { formatPrice, formatTextMaxChars, getProductSellingPrice } from '@utilities/functions';
import { BasketItem, MultiplePaymentMethod, Promotion } from '@utilities/types';
import { PaymentMethodeEnum, PromotionTypeEnum } from '@utilities/enums';
import { Banknote, CreditCard, FileText, Smartphone, Wallet, Tag } from 'lucide-react';

interface Props {
    basket: BasketItem[];
    totalBasket: number;
    paymentMethods?: MultiplePaymentMethod[];
    promotion?: Promotion | null;
    discountAmount?: number;
    discountedTotal?: number;
}

const getPaymentIcon = (methode: PaymentMethodeEnum) => {
    switch (methode) {
        case PaymentMethodeEnum.esp:
            return <Banknote className="w-3 h-3" />;
        case PaymentMethodeEnum.mobilMoney:
            return <Smartphone className="w-3 h-3" />;
        case PaymentMethodeEnum.cb:
            return <CreditCard className="w-3 h-3" />;
        case PaymentMethodeEnum.cheque:
            return <FileText className="w-3 h-3" />;
        case PaymentMethodeEnum.credit:
            return <Wallet className="w-3 h-3" />;
        default:
            return <CreditCard className="w-3 h-3" />;
    }
};

export const RecapCard: FC<Props> = ({
    basket,
    totalBasket,
    paymentMethods = [],
    promotion = null,
    discountAmount = 0,
    discountedTotal,
}) => {

    const [isOpen, setIsOpen] = useState<boolean>(true);

    // Utiliser le total avec remise si fourni, sinon le total du panier
    const finalTotal = discountedTotal ?? totalBasket;

    const totalPaid = useMemo(() =>
        paymentMethods.reduce((sum, p) => sum + p.amount, 0),
        [paymentMethods]);

    const remainingAmount = useMemo(() =>
        Math.max(0, finalTotal - totalPaid),
        [finalTotal, totalPaid]);

    const toggleCard = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    return (
        <div className="p-4 bg-white rounded-lg shadow-lg">
            {/* En-tête */}
            <div className="flex items-center justify-between mb-3 overflow-scroll">
                <h2 className="text-sm md:text-xl font-semibold mb-0">
                    Récapitulatif du panier
                </h2>
                <button
                    onClick={toggleCard}
                    className="text-sm md:text-md text-blue-500 hover:text-blue-700 focus:outline-none flex items-center"
                >
                    {isOpen ? 'Masquer' : 'Voir'}
                </button>
            </div>

            {/* Liste des produits */}
            {isOpen && (
                <div className="w-full overflow-scroll max-h-[300px]">
                    {basket && (
                        basket.map((item: any, index: number) => {
                            const lastPrice = item.product.productSellingPrices[
                                item.product.productSellingPrices.length - 1
                            ]?.price || 0;
                            const productImg = item.product.images[0];
                            const baseURl = process.env.REACT_APP_IMAGE_BASE_URL;
                            const imgURl = `${baseURl}/${productImg.fileDir}/${productImg.filename}`
                            return (
                                <div key={index} className="flex justify-between items-center mb-4">
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={imgURl}

                                            alt={item.product.name}
                                            className="w-12 h-12 md:w-20 md:h-20 object-cover rounded"
                                        />
                                        <div>
                                            <p className="font-semibold text-sm">
                                                {formatTextMaxChars(item.product.name, 15)}
                                            </p>

                                            {(item.selectedSize || item.selectedColor) && (
                                                <>
                                                    <p className="text-gray-500 text-xs">
                                                        {item.selectedSize ? `Taille: ${item.selectedSize}` : ''}
                                                        {
                                                            item.selectedColor && (
                                                                <div className="flex items-center gap-1">
                                                                    Couleur:
                                                                    <div
                                                                        className="w-4 h-4 rounded-full border"
                                                                        style={{
                                                                            backgroundColor: item.selectedColor,
                                                                            borderColor: "#000",
                                                                        }} />
                                                                </div>
                                                            )
                                                        }
                                                    </p>

                                                </>
                                            )}

                                            {/* Quantité sélectionnée */}
                                            <p className="text-gray-500 text-sm">
                                                Quantité: {item.quantity}
                                            </p>
                                            <p className="text-gray-500 text-sm">
                                                PU: {formatPrice(getProductSellingPrice(item.product))}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Prix total de la ligne */}
                                    <div>
                                        <p className="font-semibold text-sm">
                                            {formatPrice((lastPrice * item.quantity))}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

            )}

            {
                basket ? (
                    <div className="mt-4 space-y-2">
                        {/* Si promotion, afficher sous-total et remise */}
                        {promotion && promotion.isActive && discountAmount > 0 ? (
                            <>
                                <div className="flex justify-between items-center text-sm text-gray-600">
                                    <span>Sous-total:</span>
                                    <span>{formatPrice(totalBasket)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-1 text-green-600">
                                        <Tag className="w-3 h-3" />
                                        <span>
                                            Remise ({promotion.type === PromotionTypeEnum.PERCENTAGE
                                                ? `${promotion.value}%`
                                                : formatPrice(promotion.value)}
                                            )
                                        </span>
                                    </div>
                                    <span className="text-green-600 font-medium">-{formatPrice(discountAmount)}</span>
                                </div>
                                <div className="flex justify-between items-center font-semibold pt-2 border-t border-dashed">
                                    <span>Total à payer:</span>
                                    <span className="text-lg text-green-700">{formatPrice(finalTotal)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="flex justify-between items-center font-semibold">
                                <span>Total:</span>
                                <span>{formatPrice(totalBasket)}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex justify-between items-center font-semibold mt-4">
                        <div className="skeleton h-3 w-20" />
                        <div className="skeleton h-3 w-16" />
                    </div>
                )
            }

            {/* Résumé des paiements */}
            {paymentMethods.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Détail des paiements</h4>
                    <div className="space-y-1">
                        {paymentMethods.map((payment, index) => (
                            <div key={index} className="flex justify-between items-center text-sm">
                                <div className="flex items-center gap-2 text-gray-600">
                                    {getPaymentIcon(payment.methode)}
                                    <span>
                                        {payment.methode}
                                        {payment.mobilePayment && ` (${payment.mobilePayment})`}
                                    </span>
                                </div>
                                <span className="font-medium text-green-600">
                                    {formatPrice(payment.amount)}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-3 pt-2 border-t border-dashed border-gray-200">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Total payé</span>
                            <span className="font-semibold text-green-600">{formatPrice(totalPaid)}</span>
                        </div>
                        {remainingAmount > 0 && (
                            <div className="flex justify-between items-center text-sm mt-1">
                                <span className="text-gray-600">Reste à payer</span>
                                <span className="font-semibold text-red-600">{formatPrice(remainingAmount)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
